import { MAX_GUEST_FIELDS_CHAR } from '@/common/config';
import * as rule from '@/common/lib/form-valid';
import { isEmailValid, isPhoneValid } from '@/common/utils';
import type { GuestTicketForm } from '@/components/OpenRequestModal/openRequestForm.types';
import type { useI18n } from '@/plugins/i18n';

export type GuestTicketFormValidationCtx = {
  showDescriptionBox: boolean;
  minDescription: number;
  maxDescription: number;
  /** When true, a blank name is accepted. Defaults to required. */
  nameOptional?: boolean;
  /** When true, a blank email is accepted (a present value must still be valid). Defaults to required. */
  emailOptional?: boolean;
  /** When true, a blank description is accepted (a present value still enforces min/max). Defaults to required. */
  descriptionOptional?: boolean;
  /** When true (or absent/undefined), a blank phone is accepted (a present value must still be valid). Defaults to optional. */
  phoneOptional?: boolean;
};

export const fieldErrorMessage = (value: string | string[] | undefined): string => {
  if (value == null || value === '') {
    return '';
  }
  return Array.isArray(value) ? value.join('\n') : value;
};

type TranslateFn = ReturnType<typeof useI18n>['t'];

export const createGuestTicketFormValidationSuite = (
  t: TranslateFn
): rule.ValidationSuite<GuestTicketForm, GuestTicketFormValidationCtx> => ({
  name: [
    rule.c((val: string, _form: GuestTicketForm, ctx?: GuestTicketFormValidationCtx) => {
      if (ctx?.nameOptional) {
        return true;
      }
      // Trim so whitespace-only input (e.g. '   ') doesn't satisfy a required name
      // (submit logic trims it to empty anyway).
      return rule.required(val.trim());
    }, t('page.guest.name.invalid')),
  ],
  phone: [
    rule.c((val: string, _form: GuestTicketForm, ctx?: GuestTicketFormValidationCtx) => {
      // Validate the trimmed value so padded input matches what submit sends.
      const trimmed = val?.trim() ?? '';
      // phoneOptional defaults to true (undefined ⇒ optional), preserving today's behaviour.
      const isOptional = ctx?.phoneOptional !== false;
      if (!trimmed) {
        return isOptional;
      }
      return isPhoneValid(trimmed);
    }, t('page.guest.phone.invalid')),
  ],
  email: [
    rule.c((val: string, _form: GuestTicketForm, ctx?: GuestTicketFormValidationCtx) => {
      const trimmed = val?.trim() ?? '';
      if (ctx?.emailOptional && !trimmed) {
        return true;
      }
      if (!trimmed) {
        return false;
      }
      return isEmailValid(trimmed);
    }, t('page.guest.email.invalid')),
  ],
  description: [
    rule.c((val: string, _form: GuestTicketForm, ctx?: GuestTicketFormValidationCtx) => {
      if (!ctx?.showDescriptionBox) {
        return true;
      }
      // Treat whitespace-only as blank so it matches the trimmed value submit sends.
      const length = val.trim().length;
      if (ctx.descriptionOptional && !length) {
        return true;
      }
      return length >= ctx.minDescription;
    }, t('page.guest.description.invalid')),
    rule.c(
      (val: string, _form: GuestTicketForm, ctx?: GuestTicketFormValidationCtx) => {
        if (!ctx?.showDescriptionBox) {
          return true;
        }
        return val.trim().length <= ctx.maxDescription;
      },
      t('page.guest.description.exceed', { maxChar: MAX_GUEST_FIELDS_CHAR.description })
    ),
  ],
});
